import React from "react";
import { Box, Container, Typography } from "@mui/material";
import { motion } from "framer-motion";

const INK = "#0f1121";
const INK_SOFT = "rgba(15, 17, 33, 0.62)";
const INK_MUTED = "rgba(15, 17, 33, 0.55)";
const INK_FAINT = "rgba(15, 17, 33, 0.42)";
const INK_HAIRLINE = "rgba(15, 17, 33, 0.08)";
const INK_HAIRLINE_SOFT = "rgba(15, 17, 33, 0.05)";
const ACCENT = "#3854e8";
const SURFACE = "#ffffff";
const CANVAS = "#f6f6f4";

const MONO_STACK =
  "'JetBrains Mono', 'Söhne Mono', 'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace";
const SANS_STACK =
  "'sohne-var', 'Helvetica Neue', 'Arial', sans-serif";

const DEFAULT_FLOW = [
  "Bring your input — a role, a posting, or a list of your own.",
  "Record each answer aloud. We transcribe as you speak.",
  "Read written feedback the moment you finish, question by question."
];

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.07, delayChildren: 0.04 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  show: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 110, damping: 22 }
  }
};

export const inputSx = {
  marginBottom: 2,
  "& .MuiOutlinedInput-root": {
    fontSize: "1rem",
    fontWeight: 500,
    borderRadius: "14px",
    backgroundColor: "#ffffff",
    transition: "box-shadow 0.2s ease, border-color 0.2s ease",
    boxShadow: "inset 0 1px 0 rgba(15, 17, 33, 0.02)",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(15, 17, 33, 0.14)",
      transition: "border-color 0.2s ease"
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(15, 17, 33, 0.32)"
    },
    "&.Mui-focused": {
      boxShadow:
        "0 0 0 3px rgba(56, 84, 232, 0.14), inset 0 1px 0 rgba(15, 17, 33, 0.02)"
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: ACCENT,
      borderWidth: "1px"
    }
  },
  "& .MuiInputLabel-outlined": {
    fontSize: "0.95rem",
    color: INK_MUTED,
    "&.Mui-focused": { color: ACCENT }
  }
};

export const primaryButtonSx = {
  fontSize: "0.95rem",
  fontWeight: 600,
  fontFamily: SANS_STACK,
  textTransform: "none",
  letterSpacing: "0.2px",
  px: 3.5,
  py: 1.3,
  borderRadius: "12px",
  color: "#fafaf7",
  backgroundColor: INK,
  boxShadow:
    "inset 0 1px 0 rgba(255, 255, 255, 0.08), 0 8px 18px -10px rgba(15, 17, 33, 0.45)",
  transition:
    "transform 0.15s ease, box-shadow 0.2s ease, background-color 0.2s ease",
  "&:hover": {
    backgroundColor: "#1a1d36",
    transform: "translateY(-1px)",
    boxShadow:
      "inset 0 1px 0 rgba(255, 255, 255, 0.08), 0 12px 22px -10px rgba(15, 17, 33, 0.55)"
  },
  "&:active": {
    transform: "translateY(0px) scale(0.985)",
    boxShadow:
      "inset 0 1px 0 rgba(255, 255, 255, 0.08), 0 4px 10px -8px rgba(15, 17, 33, 0.5)"
  },
  "&.Mui-disabled": {
    backgroundColor: "rgba(15, 17, 33, 0.18)",
    color: "rgba(255, 255, 255, 0.85)",
    boxShadow: "none"
  }
};

const FlowList = ({ items }) => (
  <Box
    component={motion.ul}
    variants={itemVariants}
    sx={{
      listStyle: "none",
      m: 0,
      p: 0,
      borderTop: `1px solid ${INK_HAIRLINE}`
    }}
  >
    {items.map((step, i) => (
      <Box
        component="li"
        key={i}
        sx={{
          display: "grid",
          gridTemplateColumns: "auto 1fr",
          columnGap: 2.25,
          alignItems: "baseline",
          py: 1.6,
          borderBottom: `1px solid ${INK_HAIRLINE}`
        }}
      >
        <Typography
          sx={{
            fontFamily: MONO_STACK,
            fontSize: "0.72rem",
            fontWeight: 500,
            letterSpacing: "0.08em",
            color: INK_FAINT,
            fontVariantNumeric: "tabular-nums"
          }}
        >
          {String(i + 1).padStart(2, "0")}
        </Typography>
        <Typography
          sx={{
            fontFamily: SANS_STACK,
            fontSize: "0.93rem",
            lineHeight: 1.5,
            color: INK_SOFT
          }}
        >
          {step}
        </Typography>
      </Box>
    ))}
  </Box>
);

const CornerMonogram = () => (
  <Box
    aria-hidden
    sx={{
      position: "absolute",
      bottom: { xs: 32, md: 56 },
      right: { xs: 16, md: 56 },
      pointerEvents: "none",
      zIndex: 0,
      display: { xs: "none", lg: "flex" },
      flexDirection: "column",
      alignItems: "flex-end",
      gap: 1.25,
      opacity: 0.85
    }}
  >
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(6, 6px)",
        gridAutoRows: "6px",
        gap: "10px"
      }}
    >
      {Array.from({ length: 18 }).map((_, i) => (
        <Box
          key={i}
          sx={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            backgroundColor:
              i % 5 === 0 ? "rgba(56, 84, 232, 0.45)" : "rgba(15, 17, 33, 0.12)"
          }}
        />
      ))}
    </Box>
    <Typography
      sx={{
        fontFamily: MONO_STACK,
        fontSize: "0.7rem",
        letterSpacing: "0.22em",
        textTransform: "uppercase",
        color: INK_FAINT
      }}
    >
      Practice studio / 04.26
    </Typography>
  </Box>
);

const PracticePageLayout = ({
  icon,
  title,
  subtitle,
  kicker = "Practice session",
  flow = DEFAULT_FLOW,
  meta,
  aside,
  children
}) => {
  return (
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        backgroundColor: CANVAS,
        minHeight: "100vh",
        textAlign: "left",
        py: { xs: 6, md: 9 },
        px: { xs: 2, sm: 3 }
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 160,
          background:
            "linear-gradient(to bottom, #ffffff 0%, rgba(255, 255, 255, 0.6) 45%, rgba(255, 255, 255, 0) 100%)",
          pointerEvents: "none",
          zIndex: 0
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          top: -220,
          right: -180,
          width: 620,
          height: 620,
          borderRadius: "50%",
          background:
            "radial-gradient(circle at 30% 30%, rgba(228, 222, 207, 0.7), transparent 70%)",
          filter: "blur(40px)",
          pointerEvents: "none",
          zIndex: 0
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          bottom: -260,
          left: -180,
          width: 540,
          height: 540,
          borderRadius: "50%",
          background:
            "radial-gradient(circle at 50% 50%, rgba(56, 84, 232, 0.07), transparent 70%)",
          filter: "blur(40px)",
          pointerEvents: "none",
          zIndex: 0
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: "-25%",
          backgroundImage:
            "repeating-linear-gradient(135deg, rgba(15, 17, 33, 0.05) 0, rgba(15, 17, 33, 0.05) 1px, transparent 1px, transparent 22px)",
          animation: "practiceStripesDrift 32s linear infinite",
          willChange: "transform",
          pointerEvents: "none",
          zIndex: 0,
          maskImage:
            "radial-gradient(ellipse at 50% 50%, rgba(0,0,0,0.85), transparent 85%)",
          WebkitMaskImage:
            "radial-gradient(ellipse at 50% 50%, rgba(0,0,0,0.85), transparent 85%)",
          "@keyframes practiceStripesDrift": {
            "0%": { transform: "translate3d(0, 0, 0)" },
            "100%": { transform: "translate3d(-44px, -44px, 0)" }
          },
          "@media (prefers-reduced-motion: reduce)": {
            animation: "none"
          }
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: "-25%",
          backgroundImage:
            "repeating-linear-gradient(135deg, rgba(56, 84, 232, 0.07) 0, rgba(56, 84, 232, 0.07) 2px, transparent 2px, transparent 110px)",
          animation: "practiceStripesAccent 56s linear infinite",
          willChange: "transform",
          pointerEvents: "none",
          zIndex: 0,
          maskImage:
            "radial-gradient(ellipse at 30% 70%, rgba(0,0,0,0.65), transparent 75%)",
          WebkitMaskImage:
            "radial-gradient(ellipse at 30% 70%, rgba(0,0,0,0.65), transparent 75%)",
          "@keyframes practiceStripesAccent": {
            "0%": { transform: "translate3d(0, 0, 0)" },
            "100%": { transform: "translate3d(220px, 220px, 0)" }
          },
          "@media (prefers-reduced-motion: reduce)": {
            animation: "none"
          }
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "linear-gradient(rgba(15, 17, 33, 0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(15, 17, 33, 0.035) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
          maskImage:
            "radial-gradient(ellipse at 50% 0%, rgba(0,0,0,0.5), transparent 75%)",
          WebkitMaskImage:
            "radial-gradient(ellipse at 50% 0%, rgba(0,0,0,0.5), transparent 75%)",
          pointerEvents: "none",
          zIndex: 0
        }}
      />
      <CornerMonogram />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Box
          component={motion.div}
          variants={containerVariants}
          initial="hidden"
          animate="show"
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "minmax(280px, 5fr) 7fr" },
            gap: { xs: 4, md: 8 },
            alignItems: "start"
          }}
        >
          <Box
            sx={{
              position: { xs: "static", md: "sticky" },
              top: { md: 96 }
            }}
          >
            <Box
              component={motion.div}
              variants={itemVariants}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                mb: 3
              }}
            >
              {icon && (
                <Box
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: "11px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: SURFACE,
                    border: `1px solid ${INK_HAIRLINE}`,
                    color: ACCENT,
                    boxShadow:
                      "inset 0 1px 0 rgba(255, 255, 255, 0.6), 0 1px 2px rgba(15, 17, 33, 0.04)",
                    "& svg": { fontSize: 20 }
                  }}
                >
                  {icon}
                </Box>
              )}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1
                }}
              >
                <Box
                  component={motion.span}
                  animate={{ opacity: [0.55, 1, 0.55] }}
                  transition={{
                    duration: 2.4,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    backgroundColor: ACCENT,
                    display: "inline-block"
                  }}
                />
                <Typography
                  sx={{
                    fontFamily: SANS_STACK,
                    fontSize: "0.74rem",
                    fontWeight: 600,
                    letterSpacing: "0.16em",
                    textTransform: "uppercase",
                    color: INK_MUTED
                  }}
                >
                  {kicker}
                </Typography>
              </Box>
            </Box>

            <Typography
              component={motion.h1}
              variants={itemVariants}
              sx={{
                fontFamily: SANS_STACK,
                fontWeight: 600,
                fontSize: { xs: "2rem", sm: "2.35rem", md: "2.6rem" },
                lineHeight: 1.05,
                letterSpacing: "-0.025em",
                color: INK,
                m: 0,
                mb: 2.25
              }}
            >
              {title}
            </Typography>

            {subtitle && (
              <Typography
                component={motion.p}
                variants={itemVariants}
                sx={{
                  fontFamily: SANS_STACK,
                  fontSize: { xs: "1rem", md: "1.05rem" },
                  lineHeight: 1.6,
                  color: INK_SOFT,
                  maxWidth: "44ch",
                  m: 0,
                  mb: 4
                }}
              >
                {subtitle}
              </Typography>
            )}

            {flow && flow.length > 0 && <FlowList items={flow} />}

            <Box
              component={motion.div}
              variants={itemVariants}
              sx={{
                mt: 3,
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                flexWrap: "wrap"
              }}
            >
              <Typography
                sx={{
                  fontFamily: MONO_STACK,
                  fontSize: "0.7rem",
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  color: INK_FAINT
                }}
              >
                {meta || "Audio + written feedback"}
              </Typography>
              <Box
                sx={{
                  width: 4,
                  height: 4,
                  borderRadius: "50%",
                  backgroundColor: INK_HAIRLINE
                }}
              />
              <Typography
                sx={{
                  fontFamily: MONO_STACK,
                  fontSize: "0.7rem",
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  color: INK_FAINT
                }}
              >
                Save & revisit
              </Typography>
            </Box>
          </Box>

          <Box
            component={motion.div}
            variants={itemVariants}
            sx={{
              backgroundColor: SURFACE,
              borderRadius: "24px",
              border: `1px solid ${INK_HAIRLINE}`,
              boxShadow:
                "inset 0 1px 0 rgba(255, 255, 255, 0.6), 0 24px 60px -32px rgba(15, 17, 33, 0.18), 0 2px 6px -2px rgba(15, 17, 33, 0.05)",
              overflow: "hidden"
            }}
          >
            <Box sx={{ p: { xs: 2.5, sm: 4, md: 5 } }}>{children}</Box>
            {aside && (
              <Box
                sx={{
                  borderTop: `1px solid ${INK_HAIRLINE_SOFT}`,
                  backgroundColor: "rgba(15, 17, 33, 0.015)",
                  p: { xs: 2.5, sm: 4, md: 5 }
                }}
              >
                {aside}
              </Box>
            )}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default PracticePageLayout;
